import { SiApacheecharts, SiCypress, SiFigma, SiGithub, SiGmail, SiJest, SiLemonsqueezy, SiNextdotjs, SiReact, SiReactquery, SiRedux, SiReplicate, SiSass, SiShadcnui, SiSocket, SiStorybook, SiStripe, SiStyledcomponents, SiTailwindcss, SiTypescript, SiVercel, SiVite, SiWebpack, SiWebrtc } from '@icons-pack/react-simple-icons';

import {
  Briefcase, FolderGit2, Linkedin, Mail, User, Wrench,
} from 'lucide-react';
import { RecentProjectCardType } from '@/components/recent-projects';
import { BentoGridItemProps } from '@/components/ui/bento-grid';
import { WorkExperienceItem } from '@/components/work-experience';
import { SkillItem } from '@/components/skills';

export const navItems = [
  { name: 'About', link: '#about', icon: User },
  { name: 'Projects', link: '#projects', icon: FolderGit2 },
  { name: 'Work Experience', link: '#work-experience', icon: Briefcase },
  { name: 'Skills', link: '#skills', icon: Wrench },
  { name: 'Contact', link: '#contact', icon: Mail },
];

export const hero = {
  eyebrow: 'FRONTEND ENGINEER • DESIGN SYSTEMS • MOTION • PERFORMANCE',
  title: 'Building complex product UI, from Figma to production.',
  description:
    'I’m Vadym - a frontend engineer. I rebuilt a live DTC storefront solo into a headless Next.js commerce platform: custom Stripe checkout and self-service subscriptions. I go into the backend when a feature needs it.',
};

export const gridItems: BentoGridItemProps[] = [
  {
    id: 1,
    title: 'Experienced in fast-paced environments, I help businesses build scalable, high-performance web applications using React, TypeScript, and Next.js.',
    description: '',
    className: 'lg:col-span-3 md:col-span-6 md:row-span-4 lg:min-h-[50vh]',
    imgClassName: 'w-full h-full',
    titleClassName: 'justify-end',
    img: '/b1.svg',
    spareImg: '',
    variant: 'default',
  },
  {
    id: 2,
    title: 'Experienced in distributed teams - async-friendly, clear communication, predictable delivery.',
    description: '',
    className: 'lg:col-span-2 md:col-span-3 md:row-span-4 min-h-80',
    imgClassName: '',
    titleClassName: 'justify-start',
    img: '',
    spareImg: '',
    variant: 'globe',
  },
  {
    id: 3,
    title: 'Bringing Performance & Scalability to Your Frontend.',
    description: '',
    className: 'lg:col-span-2 md:col-span-3 md:row-span-1',
    imgClassName: '',
    titleClassName: 'justify-start',
    img: '/grid.svg',
    spareImg: '/b4.svg',
    variant: 'default',
  },
  {
    id: 4,
    title: 'Frontend architecture: clear module boundaries, public APIs, and patterns a team can extend without breaking things.',
    description: 'The Inside Scoop',
    className: 'md:col-span-3 md:row-span-2',
    imgClassName: 'absolute right-0 bottom-0 md:w-96 w-60',
    titleClassName: 'justify-center md:justify-start lg:justify-center',
    img: '/b5.svg',
    spareImg: '/grid.svg',
    spareImgWrapperClassName: 'w-full opacity-80',
    variant: 'default',
  },
  {
    id: 5,
    title: 'Let’s build something amazing together!',
    description: '',
    className: 'lg:col-span-2 md:col-span-3 md:row-span-1',
    imgClassName: '',
    titleClassName: 'justify-center md:max-w-full max-w-60 text-center',
    img: '',
    spareImg: '',
    wrapperClassName: 'flex justify-center relative',
    variant: 'contact',
  },
];

export const projects: RecentProjectCardType[] = [
  {
    id: 8,
    title: 'Lucetic - Headless Commerce Storefront',
    description: 'Live DTC supplement brand (lucetic.com). Solo rebuild of 30+ production routes on Next.js 16 / React 19, pixel-accurate from 100+ Figma frames, with SSR and micro-animations. Multi-step Stripe checkout with Apple Pay / Google Pay and self-serve subscriptions.',
    img: '/projects/p8.png',
    iconList: [SiNextdotjs, SiReact, SiTypescript, SiStripe, SiVercel],
    repositoryLink: '',
    liveAppLink: 'https://lucetic.com',
  },
  {
    id: 1,
    title: 'Internal Dashboard for Game Updates',
    img: '/projects/p7.png',
    iconList: [SiReact, SiTypescript, SiTailwindcss, SiShadcnui, SiReactquery],
    description: 'Developed an internal dashboard used by multiple teams (about 40 developers) to track game updates and manage file signatures at scale. Includes RVA search, signature CRUD with scan options and history, and a webhook system (event filters, headers, toggles) with on-demand sync.',
    repositoryLink: '',
    liveAppLink: '',
  },
  {
    id: 2,
    title: 'Finance dashboard',
    description: 'Personal finance dashboard with Plaid sandbox linking, account/transaction sync, analytics charts, filters, CSV import/export, auto-categorization, and subscription-ready flows via Lemon Squeezy.',
    img: '/projects/p4.png',
    iconList: [SiNextdotjs, SiTypescript, SiTailwindcss, SiShadcnui, SiLemonsqueezy, SiReactquery, SiApacheecharts],
    repositoryLink: 'https://github.com/nothing9537/financer',
    liveAppLink: 'https://financer-beta.vercel.app/',
  },
  {
    id: 3,
    title: 'Real-time Messenger with Workspace control',
    description: 'Slack-style workspace messenger with real-time channels, threads/replies, reactions, optimistic UI, invite flows, image uploads, and role-based workspace controls.',
    img: '/projects/p1.png',
    iconList: [SiNextdotjs, SiTypescript, SiTailwindcss, SiShadcnui],
    repositoryLink: 'https://github.com/nothing9537/slack-clone',
    liveAppLink: 'https://slack-clone-tau-ten.vercel.app',
  },
  {
    id: 4,
    title: 'AI Platform',
    description:
      'Multi-model AI playground with provider abstraction, reusable chat/composer primitives, streaming responses, abort/retry controls, and typed service contracts for maintainable feature expansion.',
    img: '/projects/p2.png',
    iconList: [SiNextdotjs, SiTypescript, SiReplicate, SiTailwindcss, SiShadcnui],
    repositoryLink: 'https://github.com/nothing9537/ai-platform',
    liveAppLink: 'https://ai-platform-eight.vercel.app/',
  },
  {
    id: 5,
    title: 'Freez Blood',
    description: 'Web-application for community project of game servers on CS:GO / CS2 with 1000+ concurrent users. The application has live chat, trades, trading platform, clans and much more. The project was fully developed from scratch, taking into account all customer requirements and Figma design.',
    img: '/projects/p3.png',
    iconList: [SiReact, SiTypescript, SiStyledcomponents, SiVite, SiSocket, SiFigma],
    repositoryLink: '',
    liveAppLink: 'https://fb-cs.ru/servers',
  },
  {
    id: 6,
    title: 'Real-time video / audio Messenger',
    description: 'Discord-style real-time video and audio messenger with features like voice chat, video chat, file sharing, channel creation, server management, customizable servers/channels, and role-based user management.',
    img: '/projects/p5.png',
    iconList: [SiNextdotjs, SiTypescript, SiWebrtc, SiTailwindcss, SiShadcnui],
    repositoryLink: 'https://github.com/nothing9537/discord-clone',
    liveAppLink: 'https://discord-clone-production-7510.up.railway.app',
  },
  {
    id: 7,
    title: 'Production Project',
    description: 'Although the project has no business value, but everything is done to the highest standards: testing, architecture, module interaction, future extensibility. Anyway, you can log in to your account, change the design of the application, application themes, view pre-prepared articles, and a few other features.',
    img: '/projects/p6.png',
    iconList: [SiReact, SiTypescript, SiRedux, SiSass, SiCypress, SiJest, SiStorybook, SiWebpack, SiFigma],
    repositoryLink: 'https://github.com/nothing9537/production-react-advanced-project',
    liveAppLink: 'http://216.128.146.136',
  },
];

export const workExperience: WorkExperienceItem[] = [
  // ————————— PROFESSIONAL EXPERIENCE —————————
  {
    date: 'Jan 2026 – Present',
    companyName: 'Independent',
    position: 'Product Engineer',
    companyDescription: 'Python real-time trading runtime / execution framework',
    achievements: [
      'Built a Python real-time trading runtime with FastAPI control plane, REST/WS integrations, health monitoring, emergency pause states, and snapshot/recovery flows.',
      'Designed a modular execution engine with separated inventory model, policy-based routing, exit policies, and self-healing order management.',
      'Optimized order routing and reconciliation logic, reducing observed order rejections from ~10k to ~6k and increasing fills from ~30k to ~45k in comparable 24h windows.',
      'Performed exchange network-path analysis and region-aware infrastructure placement, reducing request round-trip latency from ~500–750ms to ~8–20ms; improved Linux async I/O throughput by ~25% with uvloop.',
    ],
  },
  {
    date: 'Jun 2026 – Aug 2026',
    companyName: 'Lucetic (Contract)',
    position: 'Frontend Engineer (Headless Commerce)',
    companyDescription: 'DTC supplement brand (lucetic.com)',
    achievements: [
      'Rebuilt 30+ production routes across catalog, account, and checkout on Next.js 16 / React 19, working from 100+ Figma frames, with SSR and responsive layouts throughout.',
      'Owned SEO end to end: structured data, generated sitemaps, OG metadata, and legacy URL redirects preserved through the platform change.',
      'Built a custom multi-step Stripe checkout with saved payment methods, 3DS, Apple Pay / Google Pay, and off-session tokenization for renewals.',
      'Shipped subscription self-service (pause, resume, reschedule, quantity changes, cancellation) alongside accounts, photo reviews, and returns/RMA, backed by custom WooCommerce REST endpoints and transactional email workflows.',
      'Ran the production migration across Cloudflare, WP Engine, Vercel, and Stripe with commerce-data reconciliation and no interruption to live subscription renewals.',
    ],
  },
  {
    date: 'Oct 2025 – Jan 2026',
    companyName: 'Eleken',
    position: 'Frontend Engineer (Platform / UI Infrastructure)',
    companyDescription: 'Internal platform + AI Proposal Builder (MVP)',
    achievements: [
      'Co-defined frontend architecture with the tech lead; standardized module boundaries and reusable UI primitives.',
      'Built a Sheet/Modal system, scalable Form Kit, typed TanStack Query data layer, and reusable platform patterns.',
      'Shipped complex interactive UI: calendar views with collision logic and reusable Kanban DnD workflows.',
      'Built a TipTap-based Notion-like editor with AI-assisted targeted updates for document workflows.',
      'Improved performance via route/feature-level code splitting and dynamic imports, cutting initial load by ~40%.',
    ],
  },
  {
    date: 'Mar 2023 – Oct 2024',
    companyName: 'GODEYE LLC',
    position: 'Frontend Engineer (Trading Tools / Platform)',
    companyDescription: 'Trading R&D platform',
    achievements: [
      'Owned React/TypeScript frontend surfaces for strategy configuration, backtesting controls, and task monitoring.',
      'Designed backtesting workloads with Node Workers and multi-core execution, scaling throughput to ~1000 concurrent evaluations.',
      'Built typed exchange integration layers with REST/WS clients, reconnect/keep-alive handling, and request routing around rate/weight limits.',
      'Built a real-time Risk Engine in Node.js / NestJS using streaming market data to compute liquidation and maintenance margin metrics.',
      'Built abstractions for backtesting and execution simulation, including virtual order lifecycle handling and metrics collection.',
    ],
  },

  // ————————— SELECTED INDEPENDENT PROJECTS —————————
  // {
  //   date: 'Aug 2025 – Sep 2025',
  //   companyName: 'Financer (Independent)',
  //   position: 'Frontend Engineer',
  //   companyDescription: 'Personal finance dashboard',
  //   achievements: [
  //     'Integrated Plaid (sandbox) end-to-end: Link + secure exchange + accounts & transactions sync; analytics dashboards and CSV import/export.',
  //     'Implemented subscription paywall via Lemon Squeezy (Checkout/Portal + webhook verification) with DB-backed subscription state.',
  //   ],
  // },

  // {
  //   date: 'Sep 2024 – Nov 2024',
  //   companyName: 'Real-time Messenger (Independent)',
  //   position: 'Frontend Engineer',
  //   companyDescription: 'Slack-style messenger',
  //   achievements: [
  //     'Built real-time messaging with threads/replies, reactions, presence, and optimistic UI on Socket.io.',
  //     'Implemented workspace/channel model with RBAC, invites, and file/image uploads; hardened UX with guarded routes and error boundaries.',
  //   ],
  // },

  // {
  //   date: 'Mar 2024 – Jul 2024',
  //   companyName: 'AI Platform (Independent)',
  //   position: 'Frontend Engineer',
  //   companyDescription: 'Multi-model AI playground',
  //   achievements: [
  //     'Unified multiple AI providers behind a modular service layer; streaming responses with abort/retry and usage controls.',
  //     'Built reusable UI primitives (chat/composer/history) and typed contracts for maintainable feature expansion.',
  //   ],
  // },

  {
    date: '2023 – 2024',
    companyName: 'Freez Blood (Freelance)',
    position: 'Frontend Developer',
    companyDescription: 'Community platform (real-time + marketplace)',
    achievements: [
      'Delivered production UI from Figma: real-time chat, marketplace flows, and account features; responsive UI and custom design system.',
      'Coordinated API integration and releases with the client team.',
    ],
  },

  // ————————— DEEP DIVE / ENGINEERING PLAYGROUND —————————
  {
    date: '2023',
    companyName: 'Production React Sandbox (Independent)',
    position: 'Frontend Engineer',
    companyDescription: 'Architecture, tooling, and DX showcase',
    achievements: [
      'Built a production-grade React app around Feature-Sliced Design with clear public APIs and scalable module boundaries.',
      'Created a custom ESLint plugin enforcing architecture rules (layer imports / public API) with auto-fix.',
      'Established UI tooling: Storybook docs + testing pipeline; optimized build setup (Webpack/Vite) and CI basics.',
    ],
  },
];

export const skills: (SkillItem & BentoGridItemProps)[] = [
  {
    id: 1,
    category: 'Frontend Core',
    skills: ['TypeScript', 'JavaScript', 'React', 'Next.js', 'HTML'],
    className: 'lg:col-span-2 md:col-span-2 row-span-1',
  },
  {
    id: 2,
    category: 'UI & Interaction',
    skills: ['Tailwind CSS', 'SASS / SCSS', 'styled-components', 'shadcn-ui', 'Framer Motion', 'Storybook'],
    className: 'lg:col-span-2 md:col-span-2 row-span-1',
  },
  {
    id: 3,
    category: 'State',
    skills: ['Zustand', 'Redux Toolkit', 'Redux'],
    className: 'lg:col-span-1 md:col-span-2 row-span-1',
  },
  {
    id: 4,
    category: 'Data, Forms & Payments',
    skills: ['TanStack Query', 'REST', 'GraphQL', 'WebSockets', 'React Hook Form', 'Zod', 'Stripe'],
    className: 'lg:col-span-2 md:col-span-2 row-span-1',
  },
  {
    id: 5,
    category: 'Testing & Tooling',
    skills: ['Jest', 'React Testing Library', 'Cypress', 'Vite', 'Webpack', 'ESLint', 'Git', 'Figma'],
    className: 'lg:col-span-2 md:col-span-2 row-span-1',
  },
  {
    id: 6,
    category: 'Backend, when needed',
    skills: ['Node.js', 'NestJS', 'Python / FastAPI'],
    className: 'lg:col-span-1 md:col-span-2 row-span-1',
  },
];

export const socialMedia = [
  { link: 'https://github.com/nothing9537', icon: SiGithub, title: 'GitHub' },
  // { link: 'https://t.me/Nothingg9537', icon: SiTelegram, title: 'Telegram' },
  { link: 'https://www.linkedin.com/in/vadym-monastyrskyi', icon: Linkedin, title: 'LinkedIn' },
  { link: 'https://mail.google.com/mail/?view=cm&fs=1&to=vadym.monastyrskyi@gmail.com', icon: SiGmail, title: 'Email' },
];
